import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n, toEnPath, toEsPath } from '@/i18n/LanguageContext';
import { BRAND, brandDescription, postalAddressSchema } from '@/data/constants';


interface SeoProps {
  title: string;
  description: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}


const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel: string, href: string, hreflang?: string) => {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const Seo: React.FC<SeoProps> = ({ title, description, schema, noindex }) => {
  const { lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const full = `${title} | ${BRAND.fullName}`;
    document.title = full;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
    setMeta('property', 'og:title', full);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', BRAND.fullName);
    setMeta('name', 'twitter:card', 'summary_large_image');

    const origin = window.location.origin;
    const bare = location.pathname.replace(/^\/(en|es)(?=\/|$)/, '') || '/';
    const enPath = toEnPath(bare);
    const esPath = toEsPath(enPath);
    const enUrl = `${origin}/en${enPath === '/' ? '' : enPath}`;
    const esUrl = `${origin}/es${esPath === '/' ? '' : esPath}`;

    setLink('canonical', lang === 'es' ? esUrl : enUrl);
    setLink('alternate', enUrl, 'en');
    setLink('alternate', esUrl, 'es');
    setLink('alternate', enUrl, 'x-default');

    // Structured data
    document.querySelectorAll('script[data-seo-schema]').forEach((n) => n.remove());
    const graph: Record<string, unknown>[] = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: BRAND.fullName,
        legalName: BRAND.fullName,
        url: origin,
        email: BRAND.email,
        // Official company description — single source of truth in constants.
        description: brandDescription(lang),
        // No public telephone is published — email is the only contact channel.

        // Verified street address — machine-readable for search engines, AI
        // crawlers, security reputation vendors and enterprise IT reviewers.
        address: postalAddressSchema(),
        areaServed: 'US',
        availableLanguage: ['en', 'es'],
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            email: BRAND.email,
            areaServed: 'US',
            availableLanguage: ['en', 'es'],
          },
          {
            '@type': 'ContactPoint',
            contactType: 'security',
            email: BRAND.email,
            availableLanguage: ['en', 'es'],
          },
        ],
      },
      {
        // LocalBusiness node — reputation systems and local search resolve the
        // company to a real, physical US address from this.
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        '@id': `${origin}/#localbusiness`,
        name: BRAND.fullName,
        url: origin,
        email: BRAND.email,
        description: brandDescription(lang),
        address: postalAddressSchema(),
        areaServed: { '@type': 'State', name: 'California' },
        parentOrganization: { '@id': `${origin}/#organization` },
      },
    ];


    if (schema) {
      (Array.isArray(schema) ? schema : [schema]).forEach((s) => graph.push({ '@context': 'https://schema.org', ...s }));
    }
    graph.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    // Track AI / assistant referrals (ChatGPT, Copilot, Gemini, Perplexity)
    try {
      const ref = document.referrer.toLowerCase();
      const sources = ['chatgpt', 'openai', 'copilot', 'bing', 'gemini', 'google.com/search?udm', 'perplexity'];
      const match = sources.find((s) => ref.includes(s));
      if (match) sessionStorage.setItem('ai_referral', match);
    } catch (_e) {
      /* ignore */
    }
  }, [title, description, schema, noindex, lang, location.pathname]);

  return null;
};

export default Seo;
